// client/src/components/pages/Settings/TenantManagement.tsx (converted from .jsx, ST-00222)
//
// Settings → Account Setup → Tenant Management (global admins): organisations and
// location ownership. Audit batch IAM:
//   - IAM-10 / ST-00191 a location must have exactly one owner. The health report's
//            doubly-owned locations are listed at the top of the Location Ownership view
//            with a button to remove the wrong owner, and their rows carry an "n owners"
//            badge. (Reassigning such a row answers 409 from the server.)
//   - IAM-24 location counts are distinct names, not ownership rows; "Users" sums seats
//            (the server no longer counts Lex-Adapt staff per organisation); a bulk
//            assign that refuses some locations says which and why; a failed location
//            load is shown instead of only logged.
//   - the tier badge reads any capitalisation ('Enterprise' is stored for tenant 1).
import { useState, useEffect, useCallback, useMemo, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  MapPin,
  Plus,
  Search,
  ChevronLeft,
  Edit3,
  Ban,
  CheckCircle2,
  Users,
  X,
  AlertTriangle,
  Trash2,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import api from '../../../services/api';
import ThemedSelect from '../../common/ThemedSelect';
import { toApiDateString } from '../../../utils/dateUtils';
import type {
  BulkAssignResult,
  LocationAssignment,
  LocationHealthReport,
  LocationOwner,
  ManagedTenant,
  SettingsApiError,
  TenantPayload,
  TenantsEnvelope,
  UnassignedLocation,
} from '../../../types/clientPages/tenantManagement';

/* ═══════════════════════════════════════════════════════════════════════════
   SHARED — Animations, Dialogs, Badges
   ═══════════════════════════════════════════════════════════════════════════ */

const pageStyles = `
  @keyframes fadeInScale {
    from { opacity: 0; transform: scale(0.96) translateY(4px); }
    to   { opacity: 1; transform: scale(1) translateY(0); }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideDown {
    from { opacity: 0; transform: translateY(-8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .anim-stagger { animation: slideUp 0.35s ease-out both; }
  .tab-indicator {
    transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
`;

interface ConfirmState {
  isOpen: boolean;
  title?: string;
  message?: string;
  confirmLabel?: string;
  confirmClass?: string;
  onConfirm?: () => void | Promise<void>;
}

interface ConfirmDialogProps extends ConfirmState {
  onCancel: () => void;
}

/** The message an API failure carries (the tenants/location routes answer `{ error }`). */
const apiErrorText = (err: unknown, fallback: string): string => {
  const e = err as SettingsApiError;
  return e?.response?.data?.error || e?.response?.data?.message || e?.message || fallback;
};

// --- Confirm Dialog ---
const ConfirmDialog = ({
  isOpen,
  title,
  message,
  confirmLabel,
  confirmClass,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-2">{title}</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{message}</p>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-5 py-2 text-sm font-semibold text-white rounded-lg transition-all shadow-sm hover:shadow ${confirmClass || 'bg-red-500 hover:bg-red-600'}`}
          >
            {confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Tier Badge ---
const TIER_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
  basic: { bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400' },
  standard: { bg: 'bg-sky-50', text: 'text-sky-700', dot: 'bg-sky-500' },
  premium: { bg: 'bg-violet-50', text: 'text-violet-700', dot: 'bg-violet-500' },
  enterprise: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
};

const TierBadge = ({ tier }: { tier: string | null | undefined }) => {
  // Tiers are stored in any capitalisation ('Enterprise' for tenant 1, 'basic' elsewhere).
  const key = (tier || '').toLowerCase();
  const c = TIER_STYLES[key] || TIER_STYLES.basic!;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${c.bg} ${c.text}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Basic'}
    </span>
  );
};

// --- Capacity Bar ---
const CapacityBar = ({ current, max }: { current: number; max: number | null }) => {
  const pct = max && max > 0 ? Math.min((current / max) * 100, 100) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500';
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs tabular-nums text-slate-500 whitespace-nowrap font-medium">
        {current}
        <span className="text-slate-300">/{max}</span>
      </span>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MODALS — Tenant Create/Edit, Location Assign, Bulk Assign
   ═══════════════════════════════════════════════════════════════════════════ */

const inputClasses =
  'w-full px-3.5 py-2.5 bg-surface border border-slate-200 rounded-lg text-sm text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 transition-all outline-none';

interface TenantForm {
  tenantName: string;
  tenantCode: string;
  subscriptionTier: string;
  maxUsers: string | number;
  maxStorageMB: string | number;
  contactEmail: string;
  contactPhone: string;
  expiryDate: string;
}

interface TenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  tenant: ManagedTenant | null;
}

// --- Tenant Modal ---
const TenantModal = ({ isOpen, onClose, onSave, tenant }: TenantModalProps) => {
  const [formData, setFormData] = useState<TenantForm>({
    tenantName: '',
    tenantCode: '',
    subscriptionTier: 'basic',
    maxUsers: 5,
    maxStorageMB: 1024,
    contactEmail: '',
    contactPhone: '',
    expiryDate: '',
  });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const isEditing = !!tenant;

  useEffect(() => {
    if (isOpen) {
      if (tenant) {
        setFormData({
          tenantName: tenant.tenant_name || '',
          tenantCode: tenant.tenant_code || '',
          subscriptionTier: tenant.subscription_tier || 'basic',
          maxUsers: tenant.max_users || 5,
          maxStorageMB: tenant.max_storage_mb || 1024,
          contactEmail: tenant.contact_email || '',
          contactPhone: tenant.contact_phone || '',
          expiryDate: tenant.expiry_date ? toApiDateString(new Date(tenant.expiry_date)) : '',
        });
      } else {
        setFormData({
          tenantName: '',
          tenantCode: '',
          subscriptionTier: 'basic',
          maxUsers: 5,
          maxStorageMB: 1024,
          contactEmail: '',
          contactPhone: '',
          expiryDate: '',
        });
      }
      setFormError(null);
    }
  }, [isOpen, tenant]);

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!formData.tenantName.trim()) {
      setFormError('Tenant name is required.');
      return;
    }
    if (!isEditing && !formData.tenantCode.trim()) {
      setFormError('Tenant code is required.');
      return;
    }
    setSaving(true);
    try {
      const payload: TenantPayload = {
        tenantName: formData.tenantName.trim(),
        subscriptionTier: formData.subscriptionTier,
        maxUsers: parseInt(String(formData.maxUsers), 10) || 5,
        maxStorageMB: parseInt(String(formData.maxStorageMB), 10) || 1024,
        contactEmail: formData.contactEmail.trim() || null,
        contactPhone: formData.contactPhone.trim() || null,
        expiryDate: formData.expiryDate || null,
      };
      if (!isEditing) payload.tenantCode = formData.tenantCode.trim().toUpperCase();
      if (tenant) await api.tenants.update(tenant.tenant_id, { ...payload });
      else await api.tenants.create({ ...payload });
      onSave();
      onClose();
    } catch (err) {
      setFormError(apiErrorText(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const tiers = [
    { value: 'basic', label: 'Basic' },
    { value: 'standard', label: 'Standard' },
    { value: 'premium', label: 'Premium' },
    { value: 'enterprise', label: 'Enterprise' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {isEditing ? 'Edit Tenant' : 'Create New Tenant'}
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              {tenant ? `Updating ${tenant.tenant_code}` : 'Set up a new organization'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
          <div className="p-6 space-y-6">
            {formError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                {formError}
              </div>
            )}
            <div>
              <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
                Identity
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Organization Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.tenantName}
                    onChange={(e) => setFormData({ ...formData, tenantName: e.target.value })}
                    className={inputClasses}
                    placeholder="e.g. Acme Logistics"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Tenant Code <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.tenantCode}
                    onChange={(e) =>
                      setFormData({ ...formData, tenantCode: e.target.value.toUpperCase() })
                    }
                    disabled={isEditing}
                    className={`${inputClasses} font-mono tracking-wider ${isEditing ? 'bg-slate-50 text-slate-500 cursor-not-allowed' : ''}`}
                    placeholder="e.g. ACME"
                  />
                  {!isEditing && (
                    <p className="text-xs text-slate-400 mt-1.5">
                      Unique identifier. Cannot be changed after creation.
                    </p>
                  )}
                </div>
              </div>
            </div>
            <div>
              <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
                Plan & Limits
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Tier</label>
                  <ThemedSelect
                    value={formData.subscriptionTier}
                    onChange={(e) =>
                      setFormData({ ...formData, subscriptionTier: String(e.target.value) })
                    }
                    className={inputClasses + ' appearance-none'}
                  >
                    {tiers.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </ThemedSelect>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Max Users
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.maxUsers}
                    onChange={(e) => setFormData({ ...formData, maxUsers: e.target.value })}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Storage (MB)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.maxStorageMB}
                    onChange={(e) => setFormData({ ...formData, maxStorageMB: e.target.value })}
                    className={inputClasses}
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Expiry Date
                </label>
                <input
                  type="date"
                  value={formData.expiryDate}
                  onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                  className={inputClasses + ' max-w-[200px]'}
                />
              </div>
            </div>
            <div>
              <h3 className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-3">
                Contact
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className={inputClasses}
                    placeholder="admin@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className={inputClasses}
                    placeholder="+27 12 345 6789"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </span>
              ) : isEditing ? (
                'Update Tenant'
              ) : (
                'Create Tenant'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface AssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tenantId: number) => Promise<void>;
  tenants: ManagedTenant[];
  locationName: string;
  currentTenantId: number | null;
}

// --- Location Assign Modal ---
const AssignModal = ({
  isOpen,
  onClose,
  onSave,
  tenants,
  locationName,
  currentTenantId,
}: AssignModalProps) => {
  const [selectedTenantId, setSelectedTenantId] = useState<string | number>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isReassign = !!currentTenantId;

  useEffect(() => {
    if (isOpen) {
      setSelectedTenantId(currentTenantId || tenants[0]?.tenant_id || '');
      setError(null);
    }
  }, [isOpen, currentTenantId, tenants]);

  const handleSave = async () => {
    if (!selectedTenantId) {
      setError('Please select a tenant');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(parseInt(String(selectedTenantId), 10));
      onClose();
    } catch (err) {
      setError(apiErrorText(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">
            {isReassign ? 'Reassign Location' : 'Assign Location'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}
          <div className="mb-4">
            <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
              Location
            </label>
            <div className="px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-900 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-slate-400" />
              {locationName}
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
              Assign to
            </label>
            <ThemedSelect
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(String(e.target.value))}
              className={inputClasses}
            >
              {tenants.map((t) => (
                <option key={t.tenant_id} value={t.tenant_id}>
                  {t.tenant_name} ({t.tenant_code})
                </option>
              ))}
            </ThemedSelect>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : isReassign ? 'Reassign' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface BulkAssignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (tenantId: number) => Promise<void>;
  tenants: ManagedTenant[];
  locationCount: number;
}

// --- Bulk Assign Modal ---
const BulkAssignModal = ({
  isOpen,
  onClose,
  onSave,
  tenants,
  locationCount,
}: BulkAssignModalProps) => {
  const [selectedTenantId, setSelectedTenantId] = useState<string | number>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedTenantId(tenants[0]?.tenant_id || '');
      setError(null);
    }
  }, [isOpen, tenants]);

  const handleSave = async () => {
    if (!selectedTenantId) {
      setError('Please select a tenant');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(parseInt(String(selectedTenantId), 10));
      onClose();
    } catch (err) {
      setError(apiErrorText(err, 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="bg-surface rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden"
        style={{ animation: 'fadeInScale 0.2s ease-out' }}
      >
        <div className="px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900">Bulk Assign Locations</h3>
        </div>
        <div className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}
          <p className="text-sm text-slate-600 mb-4">
            Assign <strong className="text-slate-900">{locationCount}</strong> selected location
            {locationCount !== 1 ? 's' : ''} to a tenant.
          </p>
          <div>
            <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-1.5">
              Assign all to
            </label>
            <ThemedSelect
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(String(e.target.value))}
              className={inputClasses}
            >
              {tenants.map((t) => (
                <option key={t.tenant_id} value={t.tenant_id}>
                  {t.tenant_name} ({t.tenant_code})
                </option>
              ))}
            </ThemedSelect>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-all shadow-sm hover:shadow disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving
              ? 'Assigning...'
              : `Assign ${locationCount} Location${locationCount !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════════════════ */

export default function TenantManagement() {
  const navigate = useNavigate();

  // --- Shared state ---
  const [activeView, setActiveView] = useState<'organizations' | 'locations'>('organizations');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  // A partly successful action (bulk assign): amber, dismissible.
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<ConfirmState>({ isOpen: false });

  // --- Tenant state ---
  const [tenants, setTenants] = useState<ManagedTenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [tenantModal, setTenantModal] = useState<{ open: boolean; tenant: ManagedTenant | null }>({
    open: false,
    tenant: null,
  });

  // --- Location state ---
  const [assignments, setAssignments] = useState<LocationAssignment[]>([]);
  const [unassigned, setUnassigned] = useState<UnassignedLocation[]>([]);
  // IAM-10: locations owned by more than one tenant (GET /location-ownership/health).
  const [multiOwned, setMultiOwned] = useState<
    Array<{ location_name: string; tenants: LocationOwner[] }>
  >([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationSubTab, setLocationSubTab] = useState<'assigned' | 'unassigned'>('assigned');
  const [assignModal, setAssignModal] = useState<{
    open: boolean;
    locationName: string;
    currentTenantId: number | null;
    assignmentId: number | null;
  }>({
    open: false,
    locationName: '',
    currentTenantId: null,
    assignmentId: null,
  });
  const [selectedUnassigned, setSelectedUnassigned] = useState<Set<string>>(new Set());
  const [bulkModal, setBulkModal] = useState(false);

  // --- Data fetching ---
  const fetchTenants = useCallback(async () => {
    try {
      setTenantsLoading(true);
      const res = await api.tenants.getAllDetails();
      setTenants((res.data as TenantsEnvelope<ManagedTenant[]>)?.data || []);
    } catch (err) {
      if ((err as SettingsApiError)?.response?.status === 403)
        setError('Global admin access required.');
      else setError(apiErrorText(err, 'Failed to load organizations'));
    } finally {
      setTenantsLoading(false);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      setLocationsLoading(true);
      const [assignRes, unassignedRes, healthRes] = await Promise.all([
        api.locationOwnership.getAll(),
        api.locationOwnership.getUnassigned(),
        // The health report only adds the doubly-owned warning: its failure must not
        // hide the location lists.
        api.locationOwnership.getHealth().catch((err: unknown) => {
          console.error('Error fetching the location-ownership health report:', err);
          return null;
        }),
      ]);
      setAssignments((assignRes.data as LocationAssignment[]) || []);
      setUnassigned((unassignedRes.data as UnassignedLocation[]) || []);
      setMultiOwned((healthRes?.data as LocationHealthReport | undefined)?.multiOwned || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError(apiErrorText(err, 'Failed to load locations'));
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
    fetchLocations();
  }, [fetchTenants, fetchLocations]);

  // Reset search when switching views
  useEffect(() => {
    setSearchTerm('');
    setSelectedUnassigned(new Set());
  }, [activeView]);

  // --- Computed stats ---
  // IAM-24: count location NAMES. A location with two owner rows is one location
  // (prod: 230 ownership rows, 160 names). user_count is seats, so the sum counts
  // each person once (Lex-Adapt staff, members of every organisation, use no seat).
  const stats = useMemo(() => {
    const assignedNames = new Set(assignments.map((a) => a.location_name)).size;
    return {
      totalTenants: tenants.length,
      activeTenants: tenants.filter((t) => !t.is_suspended).length,
      totalUsers: tenants.reduce((s, t) => s + (Number(t.user_count) || 0), 0),
      totalLocations: assignedNames + unassigned.length,
      assignedLocations: assignedNames,
      unassignedLocations: unassigned.length,
    };
  }, [tenants, assignments, unassigned]);

  // --- Filtered data ---
  const filteredTenants = useMemo(() => {
    if (!searchTerm.trim()) return tenants;
    const q = searchTerm.toLowerCase();
    return tenants.filter(
      (t) =>
        (t.tenant_name || '').toLowerCase().includes(q) ||
        (t.tenant_code || '').toLowerCase().includes(q) ||
        (t.contact_email || '').toLowerCase().includes(q)
    );
  }, [tenants, searchTerm]);

  const filteredAssignments = useMemo(() => {
    if (!searchTerm.trim()) return assignments;
    const q = searchTerm.toLowerCase();
    return assignments.filter(
      (a) =>
        a.location_name.toLowerCase().includes(q) ||
        a.tenant_name?.toLowerCase().includes(q) ||
        a.tenant_code?.toLowerCase().includes(q)
    );
  }, [assignments, searchTerm]);

  const filteredUnassigned = useMemo(() => {
    if (!searchTerm.trim()) return unassigned;
    const q = searchTerm.toLowerCase();
    return unassigned.filter(
      (loc) =>
        loc.location_name?.toLowerCase().includes(q) ||
        loc.location_code?.toLowerCase().includes(q) ||
        loc.location_type?.toLowerCase().includes(q)
    );
  }, [unassigned, searchTerm]);

  // Location counts per tenant (for the organizations table)
  const locationCountByTenant = useMemo(() => {
    const map: Record<number, number> = {};
    assignments.forEach((a) => {
      map[a.tenant_id] = (map[a.tenant_id] || 0) + 1;
    });
    return map;
  }, [assignments]);

  // --- Tenant handlers ---
  const handleSuspendToggle = (tenant: ManagedTenant) => {
    const action = tenant.is_suspended ? 'reactivate' : 'suspend';
    setConfirmDialog({
      isOpen: true,
      title: `${tenant.is_suspended ? 'Reactivate' : 'Suspend'} Tenant`,
      message: `Are you sure you want to ${action} "${tenant.tenant_name}"? ${!tenant.is_suspended ? 'Users may lose access.' : 'Access will be restored.'}`,
      confirmLabel: tenant.is_suspended ? 'Reactivate' : 'Suspend',
      confirmClass: tenant.is_suspended
        ? 'bg-emerald-500 hover:bg-emerald-600'
        : 'bg-red-500 hover:bg-red-600',
      onConfirm: async () => {
        try {
          await api.tenants.suspend(tenant.tenant_id, !tenant.is_suspended);
          fetchTenants();
        } catch (err) {
          setError(apiErrorText(err, 'Failed to change the suspension'));
        }
        setConfirmDialog({ isOpen: false });
      },
    });
  };

  // --- Location handlers ---
  const handleAssign = (locationName: string) => {
    setAssignModal({ open: true, locationName, currentTenantId: null, assignmentId: null });
  };

  const handleEditAssignment = (a: LocationAssignment) => {
    setAssignModal({
      open: true,
      locationName: a.location_name,
      currentTenantId: a.tenant_id,
      assignmentId: a.location_ownership_id,
    });
  };

  const handleAssignSave = async (tenantId: number) => {
    if (assignModal.assignmentId)
      await api.locationOwnership.update(assignModal.assignmentId, { tenantId });
    else await api.locationOwnership.create({ locationName: assignModal.locationName, tenantId });
    await fetchLocations();
    setSelectedUnassigned(new Set());
  };

  const handleRemoveAssignment = (a: LocationAssignment) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Assignment',
      message: `Remove "${a.location_name}" from ${a.tenant_name}? Route visibility may be affected.`,
      confirmLabel: 'Remove',
      confirmClass: 'bg-red-500 hover:bg-red-600',
      onConfirm: async () => {
        try {
          await api.locationOwnership.delete(a.location_ownership_id);
          await fetchLocations();
        } catch (err) {
          setError(apiErrorText(err, 'Failed to remove assignment'));
        }
        setConfirmDialog({ isOpen: false });
      },
    });
  };

  // IAM-10: resolve a doubly-owned location by removing the owner that is wrong.
  const handleRemoveOwner = (
    locationName: string,
    owner: LocationOwner,
    others: LocationOwner[]
  ) => {
    const ownerName = owner.tenant_name || owner.tenant_code;
    const remaining = others.map((o) => o.tenant_name || o.tenant_code).join(' and ');
    setConfirmDialog({
      isOpen: true,
      title: 'Remove Owner',
      message: `Remove ${ownerName} as an owner of "${locationName}"? ${remaining} stays the owner. People in ${ownerName} may stop seeing routes and tickets at this location.`,
      confirmLabel: 'Remove owner',
      confirmClass: 'bg-red-500 hover:bg-red-600',
      onConfirm: async () => {
        try {
          if (owner.location_ownership_id === undefined) throw new Error('Unknown assignment');
          await api.locationOwnership.delete(owner.location_ownership_id);
          await fetchLocations();
        } catch (err) {
          setError(apiErrorText(err, 'Failed to remove the owner'));
        }
        setConfirmDialog({ isOpen: false });
      },
    });
  };

  const toggleUnassignedSelection = (name: string) => {
    setSelectedUnassigned((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedUnassigned.size === filteredUnassigned.length) setSelectedUnassigned(new Set());
    else setSelectedUnassigned(new Set(filteredUnassigned.map((loc) => loc.location_name)));
  };

  const handleBulkAssign = async (tenantId: number) => {
    const bulkAssignments = Array.from(selectedUnassigned).map((locationName) => ({
      locationName,
      tenantId,
    }));
    const res = await api.locationOwnership.bulkCreate(bulkAssignments);
    setSelectedUnassigned(new Set());
    await fetchLocations();
    // IAM-24: the server assigns what it can and lists the rest; say which and why.
    const result = (res?.data as BulkAssignResult | undefined) || {};
    const refused = result.errors || [];
    if (refused.length > 0) {
      const created = (result.created || []).length;
      const list = refused.map((e) => `${String(e.locationName ?? '?')} (${e.error})`).join('; ');
      const text = `Assigned ${created} of ${bulkAssignments.length} location${bulkAssignments.length !== 1 ? 's' : ''}. Not assigned: ${list}`;
      if (created === 0) setError(text);
      else setNotice(text);
    }
  };

  const isLoading = activeView === 'organizations' ? tenantsLoading : locationsLoading;

  // --- Render ---
  return (
    <div className="py-6 max-w-[1400px]">
      <style>{pageStyles}</style>

      {/* ── Header Zone ── */}
      <div
        className="relative mb-8 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-800 to-slate-900 dark:from-slate-100 dark:via-slate-100 dark:to-slate-50 p-8"
        style={{ animation: 'slideUp 0.4s ease-out' }}
      >
        {/* Topographic texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        {/* Breadcrumb */}
        <div className="relative flex items-center gap-1.5 text-sm mb-5">
          <button
            onClick={() => navigate('/settings')}
            className="text-slate-400 hover:text-white transition-colors font-medium"
          >
            Settings
          </button>
          <ChevronRight className="w-3 h-3 text-slate-600" />
          <span className="text-slate-500">Account Setup</span>
          <ChevronRight className="w-3 h-3 text-slate-600" />
          <span className="text-white font-semibold">Tenant Management</span>
        </div>

        {/* Title + Back */}
        <div className="relative flex items-start justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/settings')}
              className="p-2.5 text-slate-400 hover:text-white hover:bg-surface/10 rounded-xl transition-all"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Tenant Management</h1>
              <p className="text-sm text-slate-400 mt-1">
                Manage organizations and their location assignments across the platform
              </p>
            </div>
          </div>
        </div>

        {/* Stats Strip */}
        <div className="relative grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-7">
          {[
            {
              label: 'Organizations',
              value: stats.totalTenants,
              icon: Building2,
              color: 'from-slate-500/20 to-slate-600/20 border-slate-600/30',
            },
            {
              label: 'Active',
              value: stats.activeTenants,
              icon: CheckCircle2,
              color: 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30',
            },
            {
              label: 'Users',
              value: stats.totalUsers,
              icon: Users,
              color: 'from-sky-500/20 to-sky-600/20 border-sky-500/30',
            },
            {
              label: 'Locations',
              value: stats.totalLocations,
              icon: MapPin,
              color: 'from-violet-500/20 to-violet-600/20 border-violet-500/30',
            },
            {
              label: 'Assigned',
              value: stats.assignedLocations,
              icon: CheckCircle2,
              color: 'from-teal-500/20 to-teal-600/20 border-teal-500/30',
            },
            {
              label: 'Unassigned',
              value: stats.unassignedLocations,
              icon: AlertTriangle,
              color:
                stats.unassignedLocations > 0
                  ? 'from-amber-500/20 to-amber-600/20 border-amber-500/30'
                  : 'from-slate-500/20 to-slate-600/20 border-slate-600/30',
            },
          ].map((s, i) => (
            <div
              key={s.label}
              className={`bg-gradient-to-br ${s.color} border rounded-xl p-3.5 flex items-center gap-3 anim-stagger`}
              style={{ animationDelay: `${i * 0.05 + 0.15}s` }}
            >
              <s.icon className="w-4 h-4 text-slate-300 flex-shrink-0" />
              <div>
                <div className="text-xl font-bold text-white tabular-nums leading-none">
                  {s.value}
                </div>
                <div className="text-[10px] text-slate-400 mt-1 font-semibold uppercase tracking-wider">
                  {s.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">{error}</div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Partly done (bulk assign) ── */}
      {notice && (
        <div
          role="status"
          className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 flex items-start gap-3"
        >
          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">{notice}</div>
          <button
            onClick={() => setNotice(null)}
            className="text-amber-500 hover:text-amber-700"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Tab Bar + Actions ── */}
      <div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 anim-stagger"
        style={{ animationDelay: '0.3s' }}
      >
        <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
          {(
            [
              {
                id: 'organizations',
                label: 'Organizations',
                icon: Building2,
                count: stats.totalTenants,
              },
              {
                id: 'locations',
                label: 'Location Ownership',
                icon: MapPin,
                count: stats.totalLocations,
              },
            ] as Array<{
              id: 'organizations' | 'locations';
              label: string;
              icon: LucideIcon;
              count: number;
            }>
          ).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id)}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                activeView === tab.id
                  ? 'bg-surface text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              <span
                className={`ml-0.5 text-xs tabular-nums font-bold px-1.5 py-0.5 rounded-md ${
                  activeView === tab.id
                    ? 'bg-slate-100 text-slate-600'
                    : 'bg-slate-200/60 text-slate-400'
                }`}
              >
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              placeholder={
                activeView === 'organizations' ? 'Search organizations...' : 'Search locations...'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2.5 bg-surface border border-slate-200 rounded-lg text-sm placeholder-slate-400 focus:ring-2 focus:ring-teal-500/40 focus:border-teal-400 transition-all outline-none w-64"
            />
          </div>

          {/* Primary action button */}
          {activeView === 'organizations' && (
            <button
              onClick={() => setTenantModal({ open: true, tenant: null })}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-all shadow-sm hover:shadow"
            >
              <Plus className="w-3.5 h-3.5" />
              New Tenant
            </button>
          )}

          {activeView === 'locations' && selectedUnassigned.size > 0 && (
            <button
              onClick={() => setBulkModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-all shadow-sm hover:shadow"
            >
              Assign Selected ({selectedUnassigned.size})
            </button>
          )}
        </div>
      </div>

      {/* ── Locations with two owners (IAM-10, ST-00191) ── */}
      {activeView === 'locations' && multiOwned.length > 0 && (
        <section
          aria-labelledby="multi-owned-heading"
          className="mb-5 p-5 bg-amber-50 border border-amber-200 rounded-xl"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h2 id="multi-owned-heading" className="text-sm font-bold text-amber-900">
                {multiOwned.length === 1
                  ? '1 location has two owners'
                  : `${multiOwned.length} locations have two owners`}
              </h2>
              <p className="text-sm text-amber-800 mt-1">
                A location must have exactly one owner. While it has two, both organisations see
                every route and ticket at that location. Decide which organisation owns it and
                remove the other owner.
              </p>
              <ul className="mt-3 divide-y divide-amber-200/70">
                {multiOwned.map((m) => (
                  <li
                    key={m.location_name}
                    className="py-2 flex flex-col sm:flex-row sm:items-center gap-2"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-slate-900 sm:w-64">
                      <MapPin className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      {m.location_name}
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      {m.tenants.map((owner) => (
                        <span
                          key={owner.location_ownership_id ?? owner.tenant_id}
                          className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs bg-surface border border-amber-200"
                        >
                          <span className="font-semibold text-slate-700">
                            {owner.tenant_name || owner.tenant_code}{' '}
                            <span className="font-mono text-[10px] text-slate-400">
                              {owner.tenant_code}
                            </span>
                          </span>
                          <button
                            onClick={() =>
                              handleRemoveOwner(
                                m.location_name,
                                owner,
                                m.tenants.filter((o) => o !== owner)
                              )
                            }
                            className="px-2 py-0.5 rounded font-semibold text-red-600 hover:bg-red-50"
                          >
                            Remove {owner.tenant_code}
                          </button>
                        </span>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* ── Content Area ── */}
      <div
        className="bg-surface rounded-xl border border-slate-200/80 overflow-hidden shadow-sm anim-stagger"
        style={{ animationDelay: '0.35s' }}
      >
        {/* Location sub-tabs (only in locations view) */}
        {activeView === 'locations' && (
          <div className="px-5 pt-4 pb-0 border-b border-slate-100">
            <div className="flex gap-6">
              {(
                [
                  { id: 'assigned', label: 'Assigned', count: stats.assignedLocations },
                  {
                    id: 'unassigned',
                    label: 'Unassigned',
                    count: stats.unassignedLocations,
                    warning: stats.unassignedLocations > 0,
                  },
                ] as Array<{
                  id: 'assigned' | 'unassigned';
                  label: string;
                  count: number;
                  warning?: boolean;
                }>
              ).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setLocationSubTab(tab.id);
                    setSearchTerm('');
                    setSelectedUnassigned(new Set());
                  }}
                  className={`relative pb-3 text-sm font-semibold transition-colors ${
                    locationSubTab === tab.id
                      ? 'text-slate-900'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    {tab.label}
                    <span
                      className={`text-xs tabular-nums px-1.5 py-0.5 rounded-md font-bold ${
                        locationSubTab === tab.id
                          ? tab.warning
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                          : 'text-slate-400'
                      }`}
                    >
                      {tab.count}
                    </span>
                  </span>
                  {locationSubTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-600 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-slate-200 border-t-teal-600 rounded-full animate-spin" />
            <p className="text-sm text-slate-500 mt-3">Loading...</p>
          </div>
        ) : activeView === 'organizations' ? (
          /* ═════════ ORGANIZATIONS TABLE ═════════ */
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Organization
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Tier
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Users
                  </th>
                  <th className="text-center px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Routes
                  </th>
                  <th className="text-center px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Locations
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Contact
                  </th>
                  <th className="text-center px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Status
                  </th>
                  <th className="text-right px-5 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTenants.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-5 py-16 text-center">
                      <Building2 className="w-8 h-8 mx-auto mb-3 text-slate-300" />
                      <p className="text-sm font-medium text-slate-500">
                        {searchTerm ? 'No tenants match your search' : 'No tenants found'}
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredTenants.map((t, idx) => (
                    <tr
                      key={t.tenant_id}
                      className={`border-b border-slate-50 last:border-0 hover:bg-teal-50/30 transition-colors ${t.is_suspended ? 'opacity-50' : ''}`}
                      style={{ animation: `slideDown 0.3s ease-out ${idx * 0.03}s both` }}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-slate-100 to-slate-50 border border-slate-200/60 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-slate-500">
                              {(t.tenant_code || '??').slice(0, 2)}
                            </span>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              {t.tenant_name}
                            </div>
                            <div className="text-[11px] text-slate-400 font-mono tracking-wider">
                              {t.tenant_code}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <TierBadge tier={t.subscription_tier} />
                      </td>
                      <td className="px-4 py-3.5">
                        <CapacityBar current={t.user_count} max={t.max_users} />
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span className="text-sm font-semibold text-slate-700 tabular-nums">
                          {t.route_count}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        <span
                          className={`text-sm font-semibold tabular-nums ${(locationCountByTenant[t.tenant_id] || 0) > 0 ? 'text-slate-700' : 'text-slate-300'}`}
                        >
                          {locationCountByTenant[t.tenant_id] || 0}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        {t.contact_email ? (
                          <div>
                            <div className="text-sm text-slate-700">{t.contact_email}</div>
                            {t.contact_phone && (
                              <div className="text-[11px] text-slate-400 mt-0.5">
                                {t.contact_phone}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-300 italic">No contact</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-center">
                        {t.is_suspended ? (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-red-50 text-red-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                            Suspended
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-600">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setTenantModal({ open: true, tenant: t })}
                            className="p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                            title="Edit"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleSuspendToggle(t)}
                            className={`p-2 rounded-lg transition-all ${t.is_suspended ? 'text-slate-400 hover:text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:text-red-600 hover:bg-red-50'}`}
                            title={t.is_suspended ? 'Reactivate' : 'Suspend'}
                          >
                            {t.is_suspended ? (
                              <CheckCircle2 className="w-3.5 h-3.5" />
                            ) : (
                              <Ban className="w-3.5 h-3.5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Footer */}
            {filteredTenants.length > 0 && (
              <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
                <span className="text-xs text-slate-400 font-medium">
                  {filteredTenants.length} of {tenants.length} tenant
                  {tenants.length !== 1 ? 's' : ''}
                  {searchTerm && ' (filtered)'}
                </span>
              </div>
            )}
          </div>
        ) : locationSubTab === 'assigned' ? (
          /* ═════════ ASSIGNED LOCATIONS TABLE ═════════ */
          filteredAssignments.length === 0 ? (
            <div className="text-center py-16 px-6">
              <MapPin className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium text-slate-500">
                {searchTerm ? 'No matching locations' : 'No locations assigned yet'}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {searchTerm
                  ? 'Try a different search.'
                  : 'Switch to the Unassigned tab to assign locations.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-5 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Location
                    </th>
                    <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Owned By
                    </th>
                    <th className="text-right px-5 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssignments.map((a, idx) => (
                    <tr
                      key={a.location_ownership_id}
                      className="border-b border-slate-50 last:border-0 hover:bg-teal-50/30 transition-colors"
                      style={{ animation: `slideDown 0.3s ease-out ${idx * 0.02}s both` }}
                    >
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <MapPin
                            className={`w-3.5 h-3.5 flex-shrink-0 ${a.in_master_data ? 'text-emerald-500' : 'text-slate-400'}`}
                          />
                          <span className="text-sm font-semibold text-slate-900">
                            {a.location_name}
                          </span>
                          {a.location_code && (
                            <span className="text-xs text-slate-400 font-mono">
                              {a.location_code}
                            </span>
                          )}
                          {a.location_type && (
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                                a.location_type === 'Dispatch Point'
                                  ? 'bg-blue-50 text-blue-600'
                                  : a.location_type === 'Receipt Point'
                                    ? 'bg-green-50 text-green-600'
                                    : 'bg-purple-50 text-purple-600'
                              }`}
                            >
                              {a.location_type}
                            </span>
                          )}
                          {(a.owner_count ?? 1) > 1 && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-semibold bg-amber-100 text-amber-800"
                              title="This location has more than one owner. Remove the wrong owner (see the warning above)."
                            >
                              <AlertTriangle className="w-3 h-3" />
                              {`${a.owner_count} owners`}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold bg-violet-50 text-violet-700">
                          <Building2 className="w-3 h-3" />
                          {a.tenant_name}
                          <span className="text-violet-400 font-mono text-[10px]">
                            {a.tenant_code}
                          </span>
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleEditAssignment(a)}
                            className="p-2 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
                            title="Reassign"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleRemoveAssignment(a)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                            title="Remove"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-5 py-3 border-t border-slate-100">
                <span className="text-xs text-slate-400 font-medium">
                  {filteredAssignments.length} location{filteredAssignments.length !== 1 ? 's' : ''}{' '}
                  assigned
                  {searchTerm && ' (filtered)'}
                </span>
              </div>
            </div>
          )
        ) : /* ═════════ UNASSIGNED LOCATIONS TABLE ═════════ */
        filteredUnassigned.length === 0 ? (
          <div className="text-center py-16 px-6">
            <CheckCircle2 className="w-8 h-8 mx-auto mb-3 text-emerald-400" />
            <p className="text-sm font-medium text-slate-500">
              {searchTerm ? 'No matching locations' : 'All locations are assigned'}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {searchTerm
                ? 'Try a different search.'
                : 'Every discovered location has been assigned to a tenant.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-5 py-3.5 text-left w-12">
                    <input
                      type="checkbox"
                      checked={
                        selectedUnassigned.size === filteredUnassigned.length &&
                        filteredUnassigned.length > 0
                      }
                      onChange={toggleSelectAll}
                      className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                    />
                  </th>
                  <th className="text-left px-4 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Location
                  </th>
                  <th className="text-right px-5 py-3.5 text-[11px] font-semibold text-slate-400 uppercase tracking-widest">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUnassigned.map((loc, idx) => (
                  <tr
                    key={loc.location_name}
                    className="border-b border-slate-50 last:border-0 hover:bg-amber-50/30 transition-colors"
                    style={{ animation: `slideDown 0.3s ease-out ${idx * 0.02}s both` }}
                  >
                    <td className="px-5 py-3.5 w-12">
                      <input
                        type="checkbox"
                        checked={selectedUnassigned.has(loc.location_name)}
                        onChange={() => toggleUnassignedSelection(loc.location_name)}
                        className="rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                      />
                    </td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center gap-2.5">
                        {loc.in_master_data ? (
                          <MapPin className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
                        )}
                        <span className="text-sm font-semibold text-slate-900">
                          {loc.location_name}
                        </span>
                        {loc.location_code && (
                          <span className="text-xs text-slate-400 font-mono">
                            {loc.location_code}
                          </span>
                        )}
                        {loc.location_type && (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                              loc.location_type === 'Dispatch Point'
                                ? 'bg-blue-50 text-blue-600'
                                : loc.location_type === 'Receipt Point'
                                  ? 'bg-green-50 text-green-600'
                                  : 'bg-purple-50 text-purple-600'
                            }`}
                          >
                            {loc.location_type}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        onClick={() => handleAssign(loc.location_name)}
                        className="px-3.5 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 border border-teal-200/60 rounded-lg hover:bg-teal-100 transition-all"
                      >
                        Assign
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-400 font-medium">
                {filteredUnassigned.length} unassigned location
                {filteredUnassigned.length !== 1 ? 's' : ''}
              </span>
              {selectedUnassigned.size > 0 && (
                <span className="text-xs font-semibold text-teal-600">
                  {selectedUnassigned.size} selected
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      <TenantModal
        isOpen={tenantModal.open}
        onClose={() => setTenantModal({ open: false, tenant: null })}
        onSave={() => {
          fetchTenants();
          fetchLocations();
        }}
        tenant={tenantModal.tenant}
      />
      <AssignModal
        isOpen={assignModal.open}
        onClose={() =>
          setAssignModal({
            open: false,
            locationName: '',
            currentTenantId: null,
            assignmentId: null,
          })
        }
        onSave={handleAssignSave}
        tenants={tenants}
        locationName={assignModal.locationName}
        currentTenantId={assignModal.currentTenantId}
      />
      <BulkAssignModal
        isOpen={bulkModal}
        onClose={() => setBulkModal(false)}
        onSave={handleBulkAssign}
        tenants={tenants}
        locationCount={selectedUnassigned.size}
      />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={confirmDialog.confirmLabel}
        confirmClass={confirmDialog.confirmClass}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false })}
      />
    </div>
  );
}
